import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const NONE = "__none";

export default function RemapDialog({ batch, fields, onClose }) {
  const [raw, setRaw] = useState(null);
  const [mapping, setMapping] = useState({});
  const [extra, setExtra] = useState([]);
  useEffect(() => {
    if (!batch) { setRaw(null); return; }
    api.get(`/import-batches/${batch.id}/raw`).then(({ data }) => { setRaw(data); setMapping(data.mapping || {}); setExtra(data.extra_map || []); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [batch]); // eslint-disable-line
  const used = new Set([...Object.values(mapping).filter((v) => v >= 0), ...extra.map((m) => m.col)]);
  const save = async () => {
    try {
      const { data } = await api.post(`/import-batches/${batch.id}/remap`, { mapping: Object.fromEntries(Object.entries(mapping).filter(([, v]) => v >= 0)), extra_map: extra.filter((m) => m.col >= 0 && m.key) });
      toast.success(`${data.remapped} prospect(s) updated with the new mapping`); onClose(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!batch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Remap fields — {batch?.code}</DialogTitle></DialogHeader>
        {!raw ? <div className="text-sm text-slate-400 py-6 text-center">Loading original file…</div> : (
          <div className="space-y-4" data-testid="remap-dialog">
            <p className="text-xs text-slate-500">{raw.rows} rows in the original file. Changing the mapping rewrites the fields of every prospect from this batch (call history and status are kept). Columns left unmapped are shown under “More Details” on the lead page.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {fields.map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l}{["name", "mobile"].includes(k) && " *"}</Label>
                  <Select value={mapping[k] >= 0 ? String(mapping[k]) : NONE} onValueChange={(v) => setMapping({ ...mapping, [k]: v === NONE ? -1 : Number(v) })}>
                    <SelectTrigger className="h-9" data-testid={`remap-${k}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value={NONE}>— Not mapped —</SelectItem>{raw.headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h}{raw.sample[0]?.[i] ? ` · e.g. ${String(raw.sample[0][i]).slice(0, 20)}` : ""}</SelectItem>)}</SelectContent>
                  </Select></div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-[#312E81] text-sm mb-1">Rename extra columns (optional)</div>
              {extra.map((m, i) => (
                <div key={i} className="flex gap-2 mb-1">
                  <Select value={String(m.col)} onValueChange={(v) => setExtra(extra.map((x, j) => (j === i ? { ...x, col: Number(v) } : x)))}><SelectTrigger className="h-8"><SelectValue placeholder="File column" /></SelectTrigger><SelectContent className="bg-white">{raw.headers.map((h, j) => <SelectItem key={j} value={String(j)}>{h}</SelectItem>)}</SelectContent></Select>
                  <Input value={m.key} onChange={(e) => setExtra(extra.map((x, j) => (j === i ? { ...x, key: e.target.value.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase() } : x)))} placeholder="field name" className="h-8 font-mono" />
                  <button onClick={() => setExtra(extra.filter((_, j) => j !== i))} className="text-rose-500 px-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setExtra([...extra, { col: -1, key: "" }])} data-testid="remap-extra-add"><Plus className="w-3 h-3 mr-1" /> Add</Button>
            </div>
            <div className="text-[11px] text-slate-500">Unmapped columns (go to More Details): {raw.headers.filter((_, i) => !used.has(i)).join(", ") || "none"}</div>
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={!raw} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="remap-save">Apply new mapping</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
